
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { CheckCircle, Loader2 } from "lucide-react"
import { useUpdatePackageStatus } from "@/hooks/usePackages"
import { toast } from "sonner"

interface LiberarEncomendaModalProps {
  packageId: string
  uniqueCode: string
  children: React.ReactNode
}

const LiberarEncomendaModal = ({ packageId, uniqueCode, children }: LiberarEncomendaModalProps) => {
  const [open, setOpen] = useState(false)
  const [deliveredTo, setDeliveredTo] = useState("")
  const [codeInput, setCodeInput] = useState("")
  const { mutate: updateStatus, isPending } = useUpdatePackageStatus()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!deliveredTo.trim()) {
      toast.error("Por favor, informe o nome da pessoa que retirou a encomenda")
      return
    }

    if (codeInput.toUpperCase() !== uniqueCode.toUpperCase()) {
      toast.error("Código único incorreto")
      return
    }

    console.log("Tentando atualizar encomenda:", {
      packageId,
      status: "delivered",
      delivered_at: new Date().toISOString(),
      delivered_to: deliveredTo.trim()
    })

    updateStatus({
      id: packageId,
      status: "delivered",
      delivered_at: new Date().toISOString(),
      delivered_to: deliveredTo.trim()
    }, {
      onSuccess: (data) => {
        console.log("Encomenda atualizada com sucesso:", data)
        setOpen(false)
        setDeliveredTo("")
        setCodeInput("")
        toast.success("Encomenda liberada com sucesso!")
      },
      onError: (error) => {
        console.error("Erro detalhado:", error)
        toast.error(`Erro: ${error.message}`)
      }
    })
  }

  const resetForm = () => {
    setDeliveredTo("")
    setCodeInput("")
  }

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen)
      if (!newOpen) resetForm()
    }}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Liberar Encomenda</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="delivered-to">Nome da pessoa que retirou *</Label>
            <Input
              id="delivered-to"
              value={deliveredTo}
              onChange={(e) => setDeliveredTo(e.target.value)}
              placeholder="Nome completo"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="unique-code">Código único *</Label>
            <Input
              id="unique-code"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              placeholder="Digite o código único"
              required
            />
            <p className="text-sm text-gray-500">
              Código esperado: {uniqueCode}
            </p>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <CheckCircle className="h-4 w-4 mr-2" />
              Liberar Encomenda
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default LiberarEncomendaModal
